import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService, type ApiError, type SystemSettingsResponse, type UpdateSystemSettingsDto } from '../services/api';
import { SYSTEM_SETTINGS_QUERY_KEY } from '../types/systemSettings';

export interface UseSystemSettingsResult {
  settings: SystemSettingsResponse | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: ApiError | null;
  refetch: () => void;
  updateSettings: (payload: UpdateSystemSettingsDto) => Promise<SystemSettingsResponse>;
  isSaving: boolean;
  updateError: ApiError | null;
}

export function useSystemSettings(): UseSystemSettingsResult {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<SystemSettingsResponse, ApiError>({
    queryKey: SYSTEM_SETTINGS_QUERY_KEY,
    queryFn: () => apiService.getSystemSettings(),
    retry: (failureCount, err) => {
      // Do not retry on auth/permission errors
      if (err.statusCode === 401 || err.statusCode === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const {
    mutateAsync,
    isPending,
    error: mutationError,
  } = useMutation<SystemSettingsResponse, ApiError, UpdateSystemSettingsDto>({
    mutationFn: (payload) => apiService.updateSystemSettings(payload),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(SYSTEM_SETTINGS_QUERY_KEY, updatedSettings);
    },
  });

  return {
    settings: data ?? null,
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetch,
    updateSettings: mutateAsync,
    isSaving: isPending,
    updateError: mutationError ?? null,
  };
}
